"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface SalesFunnelGeneratorProps {
  className?: string;
}

export default function SalesFunnelGenerator({ className = "" }: SalesFunnelGeneratorProps) {
  const [product, setProduct] = useState("");
  const [price, setPrice] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [funnelType, setFunnelType] = useState("VENDA_DIRETA");
  const [trafficSource, setTrafficSource] = useState("MÚLTIPLAS");
  const [conversionGoal, setConversionGoal] = useState("VENDA");
  const [funnel, setFunnel] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateFunnel = async () => {
    if (!product || !price || !targetAudience) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/sales-funnel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          product, 
          price, 
          targetAudience, 
          funnelType, 
          trafficSource, 
          conversionGoal 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setFunnel(data);
        addToast("🚀 Funil de vendas gerado com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar funil", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar funil", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyStepContent = (step: any) => {
    navigator.clipboard.writeText(JSON.stringify(step, null, 2));
    addToast("📋 Etapa do funil copiada!", "success");
  };

  return (
    <div className={`bg-gradient-to-br from-orange-900 via-red-900 to-pink-900 text-white rounded-lg p-6 border border-orange-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-orange-300">
          🚀 SALES FUNNEL GENERATOR 🚀
        </h3>
        <div className="text-xs text-orange-400 mb-4">
          [FUNNEL_AUTOMATION] v3.0.0 | STATUS: ACTIVE | AUTH: SALES_MASTER
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-orange-400 mb-2">PRODUCT_NAME:</label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="w-full bg-orange-950/50 border border-orange-500/50 text-orange-300 px-3 py-2 rounded focus:outline-none focus:border-orange-400 focus:shadow-[0_0_10px_rgba(251,146,60,0.3)]"
            placeholder="ex: Curso de Marketing Digital"
          />
        </div>

        <div>
          <label className="block text-xs text-orange-400 mb-2">PRODUCT_PRICE:</label>
          <input
            type="text"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-full bg-orange-950/50 border border-orange-500/50 text-orange-300 px-3 py-2 rounded focus:outline-none focus:border-orange-400"
            placeholder="ex: 997.00"
          />
        </div>

        <div>
          <label className="block text-xs text-orange-400 mb-2">TARGET_AUDIENCE:</label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full bg-orange-950/50 border border-orange-500/50 text-orange-300 px-3 py-2 rounded focus:outline-none focus:border-orange-400"
            placeholder="ex: Empreendedores iniciantes"
          />
        </div>

        <div>
          <label className="block text-xs text-orange-400 mb-2">FUNNEL_TYPE:</label>
          <select
            value={funnelType}
            onChange={(e) => setFunnelType(e.target.value)}
            className="w-full bg-orange-950/50 border border-orange-500/50 text-orange-300 px-3 py-2 rounded focus:outline-none focus:border-orange-400"
          >
            <option value="VENDA_DIRETA">DIRECT_SALES</option>
            <option value="LANÇAMENTO">PRODUCT_LAUNCH</option>
            <option value="WEBINAR">WEBINAR_FUNNEL</option>
            <option value="APLICATIVO">APP_FUNNEL</option>
            <option value="ECOMMERCE">ECOMMERCE_FUNNEL</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-orange-400 mb-2">TRAFFIC_SOURCE:</label>
          <select
            value={trafficSource}
            onChange={(e) => setTrafficSource(e.target.value)}
            className="w-full bg-orange-950/50 border border-orange-500/50 text-orange-300 px-3 py-2 rounded focus:outline-none focus:border-orange-400"
          >
            <option value="MÚLTIPLAS">MULTI_SOURCE</option>
            <option value="FACEBOOK_ADS">FACEBOOK_ADS</option>
            <option value="GOOGLE_ADS">GOOGLE_ADS</option>
            <option value="ORGÂNICO">ORGANIC_TRAFFIC</option>
            <option value="EMAIL">EMAIL_MARKETING</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-orange-400 mb-2">CONVERSION_GOAL:</label>
          <select
            value={conversionGoal}
            onChange={(e) => setConversionGoal(e.target.value)}
            className="w-full bg-orange-950/50 border border-orange-500/50 text-orange-300 px-3 py-2 rounded focus:outline-none focus:border-orange-400"
          >
            <option value="VENDA">SALE</option>
            <option value="LEAD">LEAD_GENERATION</option>
            <option value="INSCRIÇÃO">REGISTRATION</option>
            <option value="DOWNLOAD">DOWNLOAD</option>
            <option value="AGENDAMENTO">BOOKING</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateFunnel}
        disabled={isLoading || !product || !price || !targetAudience}
        className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 px-6 rounded font-mono text-sm hover:from-orange-700 hover:to-red-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> BUILDING_FUNNEL...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            🚀 [GENERATE_FUNNEL] 🚀
          </span>
        )}
      </button>

      {funnel && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-orange-400 mb-4">
            [FUNNEL_GENERATED] | NAME: {funnel.funnel_name} | CONVERSION: {funnel.estimated_conversion}
          </div>
          
          <div className="bg-orange-950/30 border border-orange-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-orange-300 mb-3">📊 FUNNEL_OVERVIEW:</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="bg-orange-900/30 p-2 rounded">
                <span className="text-orange-400">PRODUCT:</span>
                <span className="text-orange-300 ml-2">{funnel.product}</span>
              </div>
              <div className="bg-orange-900/30 p-2 rounded">
                <span className="text-orange-400">PRICE:</span>
                <span className="text-orange-300 ml-2">R$ {funnel.price}</span>
              </div>
              <div className="bg-orange-900/30 p-2 rounded">
                <span className="text-orange-400">REVENUE:</span>
                <span className="text-orange-300 ml-2">{funnel.estimated_revenue}</span>
              </div>
            </div>
          </div>
          
          {funnel.steps?.map((step: any, index: number) => (
            <div key={index} className="bg-orange-950/30 border border-orange-500/30 rounded p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold text-orange-300">
                  STEP {step.step}: {step.name}
                </h4>
                <button
                  onClick={() => copyStepContent(step)}
                  className="text-xs bg-orange-500/20 px-2 py-1 rounded hover:bg-orange-500/30"
                >
                  [COPY_STEP]
                </button>
              </div>
              
              <div className="mb-3">
                <h5 className="text-sm font-bold text-orange-400 mb-2">TYPE:</h5>
                <div className="text-orange-300 text-sm bg-black/30 p-2 rounded font-mono">{step.type}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-orange-400 mb-2">PURPOSE:</h5>
                <div className="text-orange-300 text-sm">{step.purpose}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-orange-400 mb-2">URL:</h5>
                <div className="text-orange-300 text-sm bg-black/30 p-2 rounded font-mono">{step.url}</div>
              </div>

              {step.elements && (
                <div className="mb-3">
                  <h5 className="text-sm font-bold text-orange-400 mb-2">ELEMENTS:</h5>
                  
                  {step.elements.headline && (
                    <div className="mb-2">
                      <span className="text-orange-400 text-xs">HEADLINE:</span>
                      <div className="text-orange-300 text-sm bg-black/30 p-2 rounded mt-1">{step.elements.headline}</div>
                    </div>
                  )}

                  {step.elements.subheadline && (
                    <div className="mb-2">
                      <span className="text-orange-400 text-xs">SUBHEADLINE:</span>
                      <div className="text-orange-300 text-sm bg-black/30 p-2 rounded mt-1">{step.elements.subheadline}</div>
                    </div>
                  )}

                  {step.elements.cta_button && (
                    <div className="mb-2">
                      <span className="text-orange-400 text-xs">CTA_BUTTON:</span>
                      <div className="text-orange-300 text-sm bg-black/30 p-2 rounded mt-1">{step.elements.cta_button.text}</div>
                    </div>
                  )}

                  {step.elements.pricing_section && (
                    <div className="mb-2">
                      <span className="text-orange-400 text-xs">PRICING:</span>
                      <div className="text-orange-300 text-sm bg-black/30 p-2 rounded mt-1">
                        {step.elements.pricing_section.price_display}
                      </div>
                    </div>
                  )}

                  {step.elements.testimonials && (
                    <div className="mb-2">
                      <span className="text-orange-400 text-xs">TESTIMONIALS:</span>
                      <div className="space-y-1 mt-1">
                        {step.elements.testimonials.map((testimonial: any, tIndex: number) => (
                          <div key={tIndex} className="text-orange-300 text-xs bg-black/30 p-2 rounded">
                            "{testimonial.text}" - {testimonial.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div className="bg-orange-900/30 p-2 rounded">
                  <span className="text-orange-400">CONVERSION_RATE:</span>
                  <span className="text-orange-300 ml-2">{step.conversion_rate}</span>
                </div>
                <div className="bg-orange-900/30 p-2 rounded">
                  <span className="text-orange-400">TRAFFIC_SOURCES:</span>
                  <span className="text-orange-300 ml-2">{step.traffic_sources?.join(", ")}</span>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-orange-900/20 border border-orange-500/30 rounded p-4">
            <h4 className="text-lg font-bold text-orange-300 mb-3">🤖 AUTOMATION_SEQUENCE:</h4>
            <div className="space-y-1">
              {funnel.automation_sequence?.map((rule: any, index: number) => (
                <div key={index} className="text-orange-300 text-sm">
                  <span className="text-orange-400">TRIGGER:</span> {rule.trigger} → 
                  <span className="text-orange-400 ml-2">ACTION:</span> {rule.action} 
                  <span className="text-orange-400 ml-2">DELAY:</span> {rule.delay}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-orange-900/20 border border-orange-500/30 rounded p-4">
            <h4 className="text-lg font-bold text-orange-300 mb-3">📈 METRICS_TRACKING:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-orange-400">KPIs:</span>
                <div className="text-orange-300 mt-1">{funnel.metrics_tracking?.kpi?.join(", ")}</div>
              </div>
              <div>
                <span className="text-orange-400">TOOLS:</span>
                <div className="text-orange-300 mt-1">{funnel.metrics_tracking?.tools?.join(", ")}</div>
              </div>
            </div>
          </div>

          <div className="text-xs text-orange-400 mt-4">
            [FUNNEL_COMPLETE] | SETUP_TIME: {funnel.metadata?.estimatedSetupTime} | COMPLEXITY: {funnel.metadata?.funnelComplexity} | AUTH: ADMIN
          </div>
        </div>
      )}
    </div>
  );
}
